//src/pages/inventory/Product/AddNewProduct.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Autocomplete,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import { MEDIUM_FORM_MODAL_SX } from "../../../utils/formModalStyles";
import { createInventory } from "../../../services/inventoryService";
import { getMedicineList } from "../../../services/medicineService";
import {
  createMedicineInventory,
  updateMedicineInventory,
} from "../../../services/medicineInventoryService";
import { useSelector } from 'react-redux'; // Import useSelector

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name should be at least 2 characters long"),
  manufacturer: Yup.string()
    .required("Manufacturer is required")
    .min(2, "Manufacturer should be at least 2 characters long"),
  batchNumber: Yup.string()
    .required("Batch Number is required")
    .min(2, "Batch Number should be at least 2 characters long"),
  quantity: Yup.number()
    .required("Quantity is required")
    .integer("Quantity must be an integer")
    .min(0, "Quantity must be at least 0"),
  price: Yup.number()
    .required("Price is required")
    .min(0, "Price must be at least 0"),

  expiryDate: Yup.date()
    .required("Expiration Date is required")
    .typeError("Invalid date format"),
});

const AddNewProduct = ({ open, onClose, product_info }) => {
  const [medicine_list, setMedicine] = useState([]);
  const user = useSelector((state) => state.user.user); // Access user from Redux

  const formik = useFormik({
    initialValues: {
      name: product_info?.name || "",
      quantity: product_info?.quantity || 0,
      manufacturer: product_info?.manufacturer || "",
      batchNumber: product_info?.batchNumber || "",
      price: product_info?.price || 0,
      expiryDate: product_info?.["expiryDate"]
        ? dayjs(product_info["expiryDate"])
        : null,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const req_data = {
          ...values,
          expiryDate: dayjs(values.expiryDate).format("YYYY-MM-DD"),
          userId: user.id,
        };
        if (!!product_info) {
          const data = await updateMedicineInventory(
            product_info?.id,
            req_data
          );
        } else {
          const data = await createMedicineInventory(req_data);
        }
        handelCloseModalState();
      } catch (error) {
        console.log(error);
      }
    },
  });
  const fetchMedicine = async () => {
    try {
      const data = await getMedicineList();
      setMedicine(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchMedicine();
  }, []);

  const handelCloseModalState = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      sx={MEDIUM_FORM_MODAL_SX}
    >
      <Box display={"flex"} flexDirection={"column"}>
        <>
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            <Typography
              variant="h4"
              sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center", pb: 1.5 }}
            >
              Add Stock
            </Typography>
          </Box>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={medicine_list.map((ele) => ele.name)}
                    value={formik.values.name}
                    onChange={(event, newValue) => {
                      formik.setFieldValue(`name`, newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Select Medicine"
                        error={
                          formik.touched.name && Boolean(formik.errors.name)
                        }
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    )}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="manufacturer"
                    name="manufacturer"
                    label="Manufacturer"
                    value={formik.values.manufacturer}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.manufacturer &&
                      Boolean(formik.errors.manufacturer)
                    }
                    helperText={
                      formik.touched.manufacturer && formik.errors.manufacturer
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="batchNumber"
                    name="batchNumber"
                    label="Batch Number"
                    value={formik.values.batchNumber}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.batchNumber &&
                      Boolean(formik.errors.batchNumber)
                    }
                    helperText={
                      formik.touched.batchNumber && formik.errors.batchNumber
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="quantity"
                    name="quantity"
                    label="Quantity"
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.quantity && Boolean(formik.errors.quantity)
                    }
                    helperText={
                      formik.touched.quantity && formik.errors.quantity
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="price"
                    name="price"
                    label="Price"
                    value={formik.values.price}
                    onChange={formik.handleChange}
                    error={formik.touched.price && Boolean(formik.errors.price)}
                    helperText={formik.touched.price && formik.errors.price}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Expiry Date"
                      format="YYYY-MM-DD"
                      value={formik.values.expiryDate}
                      onChange={(value) =>
                        formik.setFieldValue("expiryDate", value)
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error:
                            formik.touched.expiryDate &&
                            Boolean(formik.errors.expiryDate),
                          helperText:
                            formik.touched.expiryDate &&
                            formik.errors.expiryDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                  <FormModalActions
                    onCancel={handelCloseModalState}
                    submitType="submit"
                    submitLabel="Create"
                    submitDisabled={formik.isSubmitting}
                    loading={formik.isSubmitting}
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
        </>
      </Box>
    </BasicModal>
  );
};

export default AddNewProduct;
